import { Link } from 'react-router-dom';
import { useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { gsap, useGSAP } from '@/lib/gsap-config';
import { ArrowRight } from 'lucide-react';

import { TemplatePreview } from './TemplatePreview';
import './template-gallery.css';

interface SlideDeckItem {
  id: string;
  category: string;
  title: string;
  subtitle: string;
  meta: string[];
  metrics: string;
}

const slideDecksVi: SlideDeckItem[] = [
  {
    id: '01',
    category: 'GIÁO DỤC SƯ PHẠM',
    title: 'Giáo án Lịch sử 12: Chiến dịch Điện Biên Phủ 1954',
    subtitle: 'Tích hợp lược đồ diễn biến chiến dịch, câu hỏi phản xạ nhanh và trích dẫn chuẩn chương trình GDPT 2018.',
    meta: ['GDPT 2018', 'Trắc nghiệm tương tác', '18 Slides'],
    metrics: 'TẠO TRONG 22 GIÂY',
  },
  {
    id: '02',
    category: 'HỌC THUẬT & KLTN',
    title: 'Bảo vệ Đồ án Tốt nghiệp: Ứng dụng Transformers trong NLP',
    subtitle: 'Định dạng chuẩn học thuật APA 7th, công thức toán học và biểu đồ ma trận đánh giá F1-score trực quan.',
    meta: ['APA 7th Edition', 'Công thức Toán LaTeX', '24 Slides'],
    metrics: 'ĐẠT 9.6/10 BẢO VỆ',
  },
  {
    id: '03',
    category: 'DOANH NGHIỆP & STARTUP',
    title: 'Series A Pitch Deck: Mạng lưới Logistics Thông minh',
    subtitle: 'Cấu trúc bài gọi vốn tiêu chuẩn Y-Combinator: Problem, Solution, TAM/SAM/SOM, Unit Economics và Dự báo tài chính.',
    meta: ['YC Framework', 'Biểu đồ Waterfall', '14 Slides'],
    metrics: '$2.5M HUY ĐỘNG',
  },
  {
    id: '04',
    category: 'BÁO CÁO CHIẾN LƯỢC',
    title: 'Báo cáo Tài chính & Kế hoạch Tuần Q3 Doanh nghiệp',
    subtitle: 'Bảng cân đối kế toán, lưu chuyển tiền tệ và bảng phân nhiệm ma trận RACI theo dõi theo thời gian thực.',
    meta: ['Liên kết Excel', 'Ma trận RACI', '16 Slides'],
    metrics: '100% PPTX GỐC',
  },
  {
    id: '05',
    category: 'CÔNG NGHỆ & HỆ THỐNG',
    title: 'Kiến trúc Hạ tầng Cloud Microservices & Bảo mật',
    subtitle: 'Sơ đồ topo hệ thống Kubernetes, luồng dữ liệu Kafka và chính sách Zero-Trust IAM rõ ràng từng lớp.',
    meta: ['System Topology', 'Độ nét cao', '20 Slides'],
    metrics: 'VECTOR CHUẨN XÁC',
  },
];

const slideDecksEn: SlideDeckItem[] = [
  {
    id: '01',
    category: 'PEDAGOGY & EDUCATION',
    title: 'History Lesson Plan: Battle of Dien Bien Phu 1954',
    subtitle: 'Tactical campaign maps, rapid interactive quizzes, and curriculum-aligned citations.',
    meta: ['Accredited', 'Interactive Quizzes', '18 Slides'],
    metrics: 'BUILT IN 22 SECONDS',
  },
  {
    id: '02',
    category: 'ACADEMIA & THESIS',
    title: 'Thesis Defense: Transformers in NLP & Linguistics',
    subtitle: 'APA 7th academic rigor, embedded LaTeX math, and confusion matrix accuracy visualizations.',
    meta: ['APA 7th Edition', 'LaTeX Math', '24 Slides'],
    metrics: 'SCORED 9.6/10 DEFENSE',
  },
  {
    id: '03',
    category: 'VENTURE & STARTUPS',
    title: 'Series A Pitch Deck: Next-Gen Autonomous Logistics',
    subtitle: 'Standard YC institutional structure: Problem, Solution, Market TAM/SAM/SOM, Unit Economics.',
    meta: ['YC Framework', 'Waterfall Charts', '14 Slides'],
    metrics: '$2.5M RAISED',
  },
  {
    id: '04',
    category: 'EXECUTIVE STRATEGY',
    title: 'Q3 Financial Health & Operational Weekly Roadmap',
    subtitle: 'Pro-forma balance sheets, dynamic cash flows, and real-time RACI accountability governance.',
    meta: ['Excel Linked', 'RACI Matrix', '16 Slides'],
    metrics: '100% NATIVE PPTX',
  },
  {
    id: '05',
    category: 'ENTERPRISE ARCHITECTURE',
    title: 'Cloud Microservices & Zero-Trust Infrastructure',
    subtitle: 'High-fidelity Kubernetes topology, Kafka pub-sub pipeline, and granular IAM policies.',
    meta: ['System Topology', 'Ultra HD Vector', '20 Slides'],
    metrics: 'PIXEL PERFECT',
  },
];

export function GalleryShowcaseSection() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language?.startsWith('en');
  const slideDecks = useMemo(() => (isEn ? slideDecksEn : slideDecksVi), [isEn]);

  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    const track = trackRef.current;
    if (!track) return;

    const media = gsap.matchMedia();
    media.add('(min-width: 1000px) and (min-height: 760px) and (prefers-reduced-motion: no-preference)', () => {
    const getScrollAmount = () => -(track.scrollWidth - window.innerWidth + 80);

    const horizontalTween = gsap.to(track, {
      x: getScrollAmount,
      ease: 'none',
      scrollTrigger: {
        trigger: containerRef.current,
        start: 'top top',
        end: () => `+=${track.scrollWidth - window.innerWidth}`,
        pin: true,
        pinSpacing: true,
        scrub: 0.8,
        invalidateOnRefresh: true,
      },
    });

    gsap.utils.toArray<HTMLElement>('.gallery-deck-card').forEach((card) => {
      gsap.fromTo(
        card,
        { scale: 0.95, opacity: 0.8 },
        {
          scale: 1,
          opacity: 1,
          duration: 0.5,
          ease: 'power1.out',
          scrollTrigger: {
            containerAnimation: horizontalTween,
            trigger: card,
            start: 'left 80%',
            end: 'right 20%',
            toggleActions: 'play reverse play reverse',
          },
        }
      );
    });
    });
    return () => media.revert();
  }, { scope: containerRef, dependencies: [isEn], revertOnUpdate: true });

  return (
    <section
      ref={containerRef}
      className="gallery-studio relative min-h-screen w-full bg-background border-b border-border/80 flex flex-col justify-center overflow-hidden py-12"
    >
      <div className="container mx-auto px-4 mb-6">
        {/* Header */}
        <div className="flex items-center justify-between font-mono text-xs text-muted-foreground uppercase tracking-widest pb-4 border-b border-border/60">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-none bg-brand inline-block" />
            <span>[{t('gallery.act')}]</span>
          </div>
          <span>{t('gallery.tag')}</span>
        </div>

        <div className="flex items-end justify-between mt-4">
          <div>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">
              {t('gallery.title')}
            </h2>
            <p className="text-sm text-muted-foreground mt-1.5">
              {t('gallery.subtitle')}
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-2 font-mono text-xs text-muted-foreground">
            <span>{t('gallery.scrollCue')}</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </div>
        </div>
      </div>

      {/* Pinned Horizontal Slider Track */}
      <div className="w-full overflow-visible py-4">
        <div
          ref={trackRef}
          className="gallery-track flex gap-6 px-4 md:px-12 w-max items-center"
          style={{ willChange: 'transform' }}
        >
          {slideDecks.map((item, index) => (
            <article key={item.id} className="gallery-deck-card template-card shrink-0">
              <Link to="/coming-soon" className="block focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand" aria-label={`${t('gallery.viewDeck')}: ${item.title}`}><TemplatePreview index={index} isEn={!!isEn} /></Link>
              <div className="template-caption">
                <span className="template-category">{item.id} / {item.category}</span>
                <h3>{item.title}</h3>
                <p>{item.subtitle}</p>
                <div className="template-meta">{item.meta.map((label) => <span key={label}>{label}</span>)}</div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export default GalleryShowcaseSection;

